import React, { useEffect, useState } from "react";
import AppModal from "components/ui/AppModal";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import AppSimpleTable from "components/ui/AppSimpleTable";
import { Spin } from "antd";
import { useGetUserWalletTransactionHistoryMutation } from "redux/loyalty/loyalty-service";
import { UTILS } from "utils";
import moment from "moment";

const CustomerDetailsModal = ({ open, onClose, customer }) => {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state) => state.LanguageSlice);
  const [getUserWalletTransactionHistory, { isLoading }] =
    useGetUserWalletTransactionHistoryMutation();
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    if (!open || !customer?._id) {
      setTransactions([]);
      return;
    }
    const brandId = UTILS.getCurrentUser()?.brandId;
    if (!brandId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getUserWalletTransactionHistory({
          userId: customer._id,
          brandId,
          limit: 120,
        });
        if (cancelled) return;
        if (res?.data?.success === 1 && Array.isArray(res.data.data)) {
          setTransactions(res.data.data);
        } else {
          setTransactions([]);
        }
      } catch {
        if (!cancelled) setTransactions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, customer?._id, getUserWalletTransactionHistory]);

  const columns = [
    {
      title: t("Type"),
      dataIndex: "type",
      key: "type",
    },
    {
      title: t("Amount"),
      dataIndex: "amount",
      key: "amount",
      render: (v) =>
        v != null && v !== ""
          ? `${t("AED")} ${parseFloat(v).toFixed(2)}`
          : "—",
    },
    {
      title: t("Balance Before"),
      dataIndex: "walletBalanceBefore",
      key: "walletBalanceBefore",
      render: (v, row) => {
        const raw =
          v ??
          row.walletBalanceBefore ??
          row.balanceBefore ??
          row.balaceBefore ??
          row.balance_before;
        if (raw == null || raw === "") return "—";
        try {
          return new Intl.NumberFormat(currentLanguage, {
            style: "currency",
            currency: "AED",
          }).format(Number(raw));
        } catch {
          return `${t("AED")} ${parseFloat(raw).toFixed(2)}`;
        }
      },
    },
    {
      title: t("Balance After"),
      dataIndex: "walletBalanceAfter",
      key: "walletBalanceAfter",
      render: (v, row) => {
        const raw =
          v ??
          row.walletBalanceAfter ??
          row.balanceAfter ??
          row.balaceAfter ??
          row.balance_after;
        if (raw == null || raw === "") return "—";
        try {
          return new Intl.NumberFormat(currentLanguage, {
            style: "currency",
            currency: "AED",
          }).format(Number(raw));
        } catch {
          return `${t("AED")} ${parseFloat(raw).toFixed(2)}`;
        }
      },
    },
    {
      title: t("Created At"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => {
        if (v == null || v === "") return "—";
        const m =
          typeof v === "number" && v < 1e12 ? moment.unix(v) : moment(v);
        return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : "—";
      },
    },
  ];

  const displayPhone = customer
    ? `+${customer.countryCode || ""}${customer.phoneNumber || ""}`
    : "";
  const lifetime =
    customer?.lifetimeSpending != null
      ? `${t("AED")} ${parseFloat(customer.lifetimeSpending).toFixed(2)}`
      : `${t("AED")} 0`;

  return (
    <AppModal open={open} title={t("Customer Details")} onClose={onClose}>
      <Spin spinning={isLoading}>
        <div className="relative grid grid-cols-2 gap-y-4 py-2">
          <div className="text-gray-900 font-semibold text-sm">{t("Customer Name")}</div>
          <div className="text-right text-gray-600 text-sm">
            {customer?.name || t("N/A")}
          </div>

          <div className="text-gray-900 font-semibold text-sm">{t("Whatsapp Number")}</div>
          <div className="text-right text-gray-600 text-sm" dir="ltr">
            {displayPhone || t("N/A")}
          </div>

          <div className="text-gray-900 font-semibold text-sm">{t("Lifetime Spending")}</div>
          <div className="text-right text-gray-600 text-sm" dir="ltr">
            {lifetime}
          </div>
        </div>

        <div className="mt-6">
          <p className="mb-2 text-sm font-semibold text-gray-900">
            {t("Loyalty transaction history")}
          </p>
          <AppSimpleTable columns={columns} data={transactions} />
        </div>
      </Spin>
    </AppModal>
  );
};

export default CustomerDetailsModal;
